import {
  Action,
  AsyncState,
  loadingAsyncState,
  none,
  Option,
  some,
  unloadedAsyncState
} from 'widgets-for-react'
import {
  getContentAuditSuspectUser,
  getSuspects,
  MarkAsNoViolationResponse,
  MarkAsViolationResponse,
  markSuspectAsNoViolation,
  markSuspectAsViolation
} from './contentAuditSuspectsApi'

export const suspectStatusses = [
  'pending',
  'no_violation',
  'violation'
] as const
export const filterableTypes = [
  'rental_advert',
  'user',
  'photo',
  'bnb_room',
  'contact_person'
] as const
export type SuspectStatus = typeof suspectStatusses[number]
export type TypeFilter = typeof filterableTypes[number]

export type SuspectsFilter = {
  status: Option<SuspectStatus>
  type: {
    [K in TypeFilter]: boolean
  }
  booking_adverts: boolean
  published_adverts: boolean
}

export const TypeFilterMapping = (): {
  [K in TypeFilter]: string[]
} => ({
  rental_advert: ['RentalAdvert::Translation', 'RentalAdvert'],
  user: ['User::Translation', 'User'],
  photo: ['Photo'],
  bnb_room: ['BnbRoom', 'BnbRoom::Translation'],
  contact_person: ['ContactPerson']
})

export type LayoutProps = {
  state: State
  onPageChange: (_: number) => void
  onFilterChange: (_: Action<SuspectsFilter>) => void
  onToggleMarkViolationForm: (_: ContentAuditSuspect) => void
  onToggleNoViolationForm: (_: ContentAuditSuspect) => void
  onToggleStrike: (_: boolean) => void
  onViolationSubmit: () => void
  onNoViolationSubmit: () => void
}

export type ViolationState = {
  suspect: ContentAuditSuspect
  strike: boolean
  markViolation: AsyncState<MarkAsViolationResponse>
  user: AsyncState<ContentAuditSuspectUser>
}

export type NoViolationState = {
  suspect: ContentAuditSuspect
  markNoViolation: AsyncState<MarkAsNoViolationResponse>
}

export type DecisionState =
  | {
      kind: 'violation'
      value: ViolationState
    }
  | {
      kind: 'no_violation'
      value: NoViolationState
    }

export type State =
  | {
      kind: 'loading'
      suspects: AsyncState<PagedContentAuditSuspects>
      filter: SuspectsFilter
    }
  | {
      kind: 'loaded'
      suspects: PagedContentAuditSuspects
      filter: SuspectsFilter
      decision: Option<DecisionState>
    }

export type Props = {}

export type PagedContentAuditSuspects = {
  page: number
  per_page: number
  total_items: number
  suspects: ContentAuditSuspect[]
}

export type ContentAuditSuspect = {
  id: number
  resource_type: string
  status: SuspectStatus
  match: string
  source: string
  user_id: number
}

export type ContentAuditSuspectUser = {
  id: number
  violation_strikes: number
  full_name: string
}

const initialFilters = (): SuspectsFilter => ({
  status: some('pending'),
  type: {
    rental_advert: true,
    user: false,
    photo: true,
    bnb_room: false,
    contact_person: false
  },
  booking_adverts: true,
  published_adverts: true
})

export const initialState = (_: Props): State => ({
  kind: 'loading',
  suspects: loadingAsyncState(() => getSuspects(initialFilters(), 0)),
  filter: initialFilters()
})

export const contentAuditSuspectsUpdaters = {
  onSuspectsUpdate: (
    updater: Action<AsyncState<PagedContentAuditSuspects>>
  ): Action<State> => s => {
    if (s.kind !== 'loading') {
      return s
    }
    const suspects = updater(s.suspects)

    return suspects.kind === 'loaded'
      ? {
          kind: 'loaded',
          suspects: suspects.value,
          filter: s.filter,
          markAsNoViolation: unloadedAsyncState(),
          markAsViolation: unloadedAsyncState(),
          decision: none()
        }
      : {
          ...s,
          suspects
        }
  },
  onPageChange: (updater: Action<number>): Action<State> => s =>
    s.kind !== 'loaded' || updater(s.suspects.page) === s.suspects.page
      ? s
      : {
          kind: 'loading',
          suspects: loadingAsyncState(_ =>
            getSuspects(s.filter, updater(s.suspects.page))
          ),
          filter: s.filter
        },
  onFilterChange: (updater: Action<SuspectsFilter>): Action<State> => s => {
    const filter = updater(s.filter)
    return {
      kind: 'loading',
      filter,
      suspects: loadingAsyncState(_ => getSuspects(filter, 0))
    }
  },
  onToggleMarkNoViolationForm: (
    suspect: ContentAuditSuspect
  ): Action<State> => s =>
    s.kind === 'loaded'
      ? {
          ...s,
          decision:
            s.decision.v.kind === 'r' &&
            s.decision.v.v.kind === 'no_violation' &&
            s.decision.v.v.value.suspect.id === suspect.id
              ? none()
              : some({
                  kind: 'no_violation',
                  value: {
                    suspect,
                    markNoViolation: unloadedAsyncState()
                  }
                })
        }
      : s,
  onToggleMarkViolationForm: (
    suspect: ContentAuditSuspect
  ): Action<State> => s =>
    s.kind === 'loaded'
      ? {
          ...s,
          decision:
            s.decision.v.kind === 'r' &&
            s.decision.v.v.kind === 'violation' &&
            s.decision.v.v.value.suspect.id === suspect.id
              ? none()
              : some({
                  kind: 'violation',
                  value: {
                    suspect,
                    strike: false,
                    markViolation: unloadedAsyncState(),
                    user: loadingAsyncState(() =>
                      getContentAuditSuspectUser(suspect)
                    )
                  }
                })
        }
      : s,
  onMarkViolationUpdate: (
    updater: Action<AsyncState<MarkAsViolationResponse>>
  ): Action<State> => s => {
    if (
      s.kind !== 'loaded' ||
      s.decision.v.kind === 'l' ||
      s.decision.v.v.kind !== 'violation'
    ) {
      return s
    }
    const violation = updater(s.decision.v.v.value.markViolation)
    if (violation.kind === 'failed') {
      alert('Something went wrong')
    }

    return violation.kind === 'loaded'
      ? {
          kind: 'loading',
          filter: s.filter,
          suspects: loadingAsyncState(_ =>
            getSuspects(s.filter, s.suspects.page)
          )
        }
      : {
          ...s,
          decision: some({
            ...s.decision.v.v,
            value: {
              ...s.decision.v.v.value,
              markViolation: violation
            }
          })
        }
  },
  onMarkNoViolationUpdate: (
    updater: Action<AsyncState<MarkAsNoViolationResponse>>
  ): Action<State> => s => {
    if (
      s.kind !== 'loaded' ||
      s.decision.v.kind === 'l' ||
      s.decision.v.v.kind !== 'no_violation'
    ) {
      return s
    }
    const noViolation = updater(s.decision.v.v.value.markNoViolation)
    if (noViolation.kind === 'failed') {
      alert('Something went wrong')
    }

    return noViolation.kind === 'loaded'
      ? {
          kind: 'loading',
          filter: s.filter,
          suspects: loadingAsyncState(_ =>
            getSuspects(s.filter, s.suspects.page)
          )
        }
      : {
          ...s,
          decision: some({
            ...s.decision.v.v,
            value: {
              ...s.decision.v.v.value,
              markNoViolation: noViolation
            }
          })
        }
  },
  onToggleStrike: (updater: Action<boolean>): Action<State> => s =>
    s.kind === 'loaded' &&
    s.decision.v.kind === 'r' &&
    s.decision.v.v.kind === 'violation'
      ? {
          ...s,
          decision: some({
            ...s.decision.v.v,
            value: {
              ...s.decision.v.v.value,
              strike: updater(s.decision.v.v.value.strike)
            }
          })
        }
      : s,
  onViolationSubmit: (): Action<State> => s =>
    s.kind === 'loaded' &&
    s.decision.v.kind === 'r' &&
    s.decision.v.v.kind === 'violation'
      ? {
          ...s,
          decision: some({
            ...s.decision.v.v,
            value: {
              ...s.decision.v.v.value,
              markViolation: loadingAsyncState(() =>
                s.kind === 'loaded' &&
                s.decision.v.kind === 'r' &&
                s.decision.v.v.kind === 'violation'
                  ? markSuspectAsViolation(
                      s.decision.v.v.value.suspect,
                      s.decision.v.v.value.strike
                    )
                  : null
              )
            }
          })
        }
      : s,
  onNoViolationSubmit: (): Action<State> => s =>
    s.kind === 'loaded' &&
    s.decision.v.kind === 'r' &&
    s.decision.v.v.kind === 'no_violation'
      ? {
          ...s,
          decision: some({
            ...s.decision.v.v,
            value: {
              ...s.decision.v.v.value,
              markNoViolation: loadingAsyncState(() =>
                s.kind === 'loaded' && s.decision.v.kind === 'r'
                  ? markSuspectAsNoViolation(s.decision.v.v.value.suspect)
                  : null
              )
            }
          })
        }
      : s,
  onSuspectUserUpdate: (
    updater: Action<AsyncState<ContentAuditSuspectUser>>
  ): Action<State> => s =>
    s.kind === 'loaded' &&
    s.decision.v.kind === 'r' &&
    s.decision.v.v.kind === 'violation'
      ? {
          ...s,
          decision: some({
            ...s.decision.v.v,
            value: {
              ...s.decision.v.v.value,
              user: updater(s.decision.v.v.value.user)
            }
          })
        }
      : s
}
